import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isMobile } from 'react-device-detect';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import PrimaryButton from 'common/components/buttons/primaryButton';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import LoadingProgress from 'common/components/progress/loading';
import { getSettings, updateSettings } from './guestControlActions';
import { GUEST_FEATURE_STATION_IDS } from 'config/guestFeature';

export function GuestControl(props) {
  const { settings, settingsLoading, settingsSaving } = props.guestControl;
  const stationId = props.auth?.user?.establishments?.[0]?.stations?.[0]?.id;
  const stationPermitida = GUEST_FEATURE_STATION_IDS.includes(stationId);

  const [enabled, setEnabled] = useState(false);
  const [limit, setLimit] = useState(0);
  const [limitByAudienceEnabled, setLimitByAudienceEnabled] = useState(false);

  const onForbidden = useCallback(() => {
    if (props.router?.push) props.router.push('/owner');
  }, [props.router]);

  useEffect(() => {
    if (stationId) props.getSettings(stationId, { onForbidden });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  useEffect(() => {
    setEnabled(!!settings.guest_feature_enabled);
    setLimit(settings.friend_invite_limit || 0);
    setLimitByAudienceEnabled(!!settings.limit_by_audience_enabled);
  }, [
    settings.guest_feature_enabled,
    settings.friend_invite_limit,
    settings.limit_by_audience_enabled,
  ]);

  const handleSave = () => {
    if (!stationPermitida) return;
    const parsed = parseInt(limit, 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      return;
    }
    props.updateSettings(stationId, {
      guest_feature_enabled: enabled,
      friend_invite_limit: parsed,
      limit_by_audience_enabled: enabled && limitByAudienceEnabled,
    });
  };

  return (
    <section id="guest-control" style={{ width: isMobile ? '90vw' : undefined }}>
      <GeneralGrid container spacing={3}>
        <GeneralGrid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Controle de convidados
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Para ver a lista e filtros de convidados, acesse{' '}
            <a href="#/owner/usuarios/convidados">Usuários → Convidados</a>.
          </Typography>
        </GeneralGrid>

        <GeneralGrid item xs={12}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Configurações
            </Typography>

            {settingsLoading ? (
              <Box display="flex" justifyContent="center" my={3}>
                <LoadingProgress />
              </Box>
            ) : (
              <GeneralGrid container spacing={3}>
                <GeneralGrid item xs={12} md={8}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        color="primary"
                        disabled={!stationPermitida}
                      />
                    }
                    label="Permitir convidados nesta estação"
                  />
                  <Typography variant="caption" display="block" color="textSecondary">
                    Ao desativar, todos os amigos e dependentes perdem acesso imediato ao app —
                    vínculos ficam preservados pra quando a feature for religada.
                  </Typography>
                </GeneralGrid>
                <GeneralGrid item xs={12} md={4}>
                  <TextField
                    label="Limite de amigos por filiado"
                    type="number"
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                    inputProps={{ min: 0, max: 100 }}
                    disabled={!enabled || !stationPermitida}
                    fullWidth
                  />
                </GeneralGrid>

                <GeneralGrid item xs={12} md={8}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={limitByAudienceEnabled}
                        onChange={(e) => setLimitByAudienceEnabled(e.target.checked)}
                        color="primary"
                        disabled={!enabled || !stationPermitida}
                      />
                    }
                    label="Limites de participação diferentes por audiência"
                  />
                  <Typography variant="caption" display="block" color="textSecondary">
                    Permite definir, em cada atividade, limites separados para filiados, amigos e
                    dependentes.
                  </Typography>
                </GeneralGrid>
                <GeneralGrid
                  item
                  xs={12}
                  md={4}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <PrimaryButton
                    onClick={handleSave}
                    disabled={settingsSaving || !stationPermitida}>
                    {settingsSaving ? 'Salvando...' : 'Salvar configurações'}
                  </PrimaryButton>
                </GeneralGrid>
              </GeneralGrid>
            )}
          </Paper>
        </GeneralGrid>
      </GeneralGrid>
    </section>
  );
}

GuestControl.propTypes = {
  guestControl: PropTypes.shape({
    settings: PropTypes.shape({
      guest_feature_enabled: PropTypes.bool,
      friend_invite_limit: PropTypes.number,
      limit_by_audience_enabled: PropTypes.bool,
    }),
    settingsLoading: PropTypes.bool,
    settingsSaving: PropTypes.bool,
  }),
  auth: PropTypes.object,
  router: PropTypes.shape({ push: PropTypes.func }),
  getSettings: PropTypes.func,
  updateSettings: PropTypes.func,
};

const mapStateToProps = (state) => ({
  guestControl: state.guestControl,
  auth: state.auth,
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ getSettings, updateSettings }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(GuestControl);
